import React, { useEffect, useState } from 'react';
import api from '../../../../utils/api';
import { IMyReportsList, IReportExtended, ISMResponseSuccess } from '../../../../models';
import { useTranslation } from 'react-i18next';
import {
    MdConstruction,
    MdHelpOutline,
    MdOutlineCheckCircleOutline,
    MdOutlineReportProblem,
    MdOutlineThumbUpAlt,
    MdRemoveCircleOutline,
} from 'react-icons/md';
import i18next from 'i18next';
import { useVisible } from 'react-hooks-visible';
import Modal from '../../../../components/Modal';
import ModalImage from '../../../../components/ModalImage';

export default function MyReportsTab() {
    const [reports, setReports] = useState<IReportExtended[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number | undefined>(undefined);
    const [endOfReportsRef, fetchMoreReports] = useVisible<HTMLDivElement>();
    const [selectedReport, setSelectedReport] = useState<IReportExtended | undefined>(undefined);

    async function fetchReports() {
        console.log(pageCount);
        if (!pageCount || currentPage < pageCount) {
            const response = await api.get(
                `/users/me/reports?resultsNumber=10&page=${currentPage}&sortBy=1`,
            );
            const responseJSON = (await response.json()) as ISMResponseSuccess<IMyReportsList>;
            const reportsSummary = reports.concat(responseJSON.data.reports.reports);
            setReports(reportsSummary);
            setPageCount(responseJSON.data.reports.pagesCount);
        }
    }

    useEffect(() => {
        if (fetchMoreReports) {
            console.log('Loading reports');
            setCurrentPage(currentPage + 1);
            fetchReports();
        }
    }, [fetchMoreReports]);

    return (
        <>
            <ReportModal
                selectedReport={selectedReport}
                onClose={() => {
                    setSelectedReport(undefined);
                }}
            />
            <div className={'flex flex-col gap-5'}>
                {reports.map((report) => {
                    return (
                        <MyReport
                            onClick={() => setSelectedReport(report)}
                            report={report}
                            key={report.id}
                        />
                    );
                })}
                <div ref={endOfReportsRef} />
            </div>
        </>
    );
}

function ReportModal(props: { selectedReport: IReportExtended | undefined; onClose: () => void }) {
    const { t } = useTranslation();
    return (
        <Modal showClose={true} width={800} show={!!props.selectedReport} onClose={props.onClose}>
            {props.selectedReport && (
                <div className={'mx-2 my-4 md:mx-10 md:my-10'}>
                    <img
                        alt={'report position'}
                        className={`mb-3 w-full`}
                        src={`https://maps.googleapis.com/maps/api/staticmap?markers=color:0xF7AA45|${props.selectedReport.localization.lat},${props.selectedReport.localization.lon}&size=600x300&scale=2&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&language=${i18next.language}&style=feature:poi|visibility:off`}
                    />

                    <div className={'my-2 ml-auto w-fit rounded bg-gray-100 px-2 py-1'}>
                        <p
                            className={`my-0 inline-flex items-center justify-center text-sm  ${
                                props.selectedReport.status === 0
                                    ? 'text-gray-500'
                                    : props.selectedReport.status === 1
                                    ? 'text-blue-500'
                                    : props.selectedReport.status === 2
                                    ? 'text-yellow-500'
                                    : props.selectedReport.status === 3
                                    ? 'text-green-500'
                                    : props.selectedReport.status === 4 && 'text-red-500'
                            }`}>
                            <span className={'mr-1 font-medium text-black'}>
                                {t('translation:MY_ACCOUNT.MY_REPORTS.STATUS')}:
                            </span>
                            {t(`translation:REPORT_STATUS.${props.selectedReport.status}`)}

                            <span className={'ml-1 text-lg'}>
                                {props.selectedReport.status === 0 && <MdHelpOutline />}
                                {props.selectedReport.status === 1 && <MdOutlineThumbUpAlt />}
                                {props.selectedReport.status === 2 && <MdConstruction />}
                                {props.selectedReport.status === 3 && (
                                    <MdOutlineCheckCircleOutline />
                                )}
                                {props.selectedReport.status === 4 && <MdRemoveCircleOutline />}
                            </span>
                        </p>
                    </div>
                    {props.selectedReport.status === 4 && (
                        <div
                            className={
                                'break-words rounded border-2 border-red-600 bg-red-200 py-1 px-2 text-sm'
                            }>
                            <b>{t('translation:MY_ACCOUNT.MY_REPORTS.REMOVE_REASON')}:</b>{' '}
                            {props.selectedReport.removeReason
                                ? props.selectedReport.removeReason
                                : t('translation:MY_ACCOUNT.MY_REPORTS.NOT_PROVIDED')}
                        </div>
                    )}

                    <div className={'mr-auto'}>
                        <p className={'my-0.5 break-words text-sm'}>
                            <b>{t('translation:MY_ACCOUNT.MY_REPORTS.TITLE')}:</b>{' '}
                            {props.selectedReport.title}
                        </p>
                        <p className={'my-0.5 text-sm'}>
                            <b>{t('translation:MY_ACCOUNT.MY_REPORTS.CATEGORY')}:</b>{' '}
                            {t(`translation:REPORT_TYPE.${props.selectedReport.category}`)}
                        </p>
                        <p className={'my-0.5 break-words text-sm'}>
                            <b>{t('translation:MY_ACCOUNT.MY_REPORTS.DESCRIPTION')}:</b>{' '}
                            {props.selectedReport.description}
                        </p>
                        <p className={'my-0.5 text-sm'}>
                            <b>{t('translation:MY_ACCOUNT.MY_REPORTS.LOCATION')}:</b>{' '}
                            {props.selectedReport.localization.label}
                        </p>
                        <p className={'my-0.5 text-sm'}>
                            <b>{t('translation:MY_ACCOUNT.MY_REPORTS.REPORT_DATE')}:</b>{' '}
                            {new Date(props.selectedReport.createdAt).toLocaleDateString(
                                i18next.language,
                                {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                },
                            )}
                        </p>
                    </div>

                    <div className={'flex gap-2'}>
                        {props.selectedReport?.attachments.map((attachmentID) => {
                            return (
                                <div
                                    key={`image-${attachmentID}`}
                                    className={'mt-2 rounded border p-1'}>
                                    <ModalImage
                                        notChangeOverflow={true}
                                        className={'w-[120px]'}
                                        image={
                                            process.env.REACT_APP_CDN_URL + '/files/' + attachmentID
                                        }
                                        alt={'report attachment'}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </Modal>
    );
}

function MyReport(props: { report: IReportExtended; onClick: () => void }) {
    const report = props.report;
    const { t } = useTranslation();
    return (
        <div
            onClick={props.onClick}
            className={
                'flex cursor-pointer flex-col gap-4 overflow-hidden px-4 py-4 shadow md:flex-row'
            }>
            <div className={'flex w-full items-center justify-center py-5 md:w-[120px] md:py-0 '}>
                {report.attachments[0] ? (
                    <img
                        alt={'report image'}
                        className={'h-fit w-[250px] md:w-[120px]'}
                        src={process.env.REACT_APP_CDN_URL + '/files/' + report.attachments[0]}
                    />
                ) : (
                    <MdOutlineReportProblem className={'text-6xl'} />
                )}
            </div>

            <div className={'flex flex-col justify-center text-sm'}>
                <p className={'my-0.5 text-sm'}>
                    <b>{t('translation:MY_ACCOUNT.MY_REPORTS.TITLE')}:</b> {report.title}
                </p>
                <p className={'my-0.5 text-sm'}>
                    <b>{t('translation:MY_ACCOUNT.MY_REPORTS.CATEGORY')}:</b>{' '}
                    {t(`translation:REPORT_TYPE.${report.category}`)}
                </p>
                <p className={'my-0.5 text-sm'}>
                    <b>{t('translation:MY_ACCOUNT.MY_REPORTS.DESCRIPTION')}:</b>{' '}
                    {report.description.slice(0, 30)}
                    {report.description.length > 30 && '...'}
                </p>
                <p className={'my-0.5 text-sm'}>
                    <b>{t('translation:MY_ACCOUNT.MY_REPORTS.LOCATION')}:</b>{' '}
                    {report.localization.label}
                </p>
            </div>

            <div className={'ml-auto flex flex-col'}>
                <div
                    className={`inline-flex items-center ${
                        report.status === 0
                            ? 'text-gray-500'
                            : report.status === 1
                            ? 'text-blue-500'
                            : report.status === 2
                            ? 'text-yellow-500'
                            : report.status === 3
                            ? 'text-green-500'
                            : report.status === 4 && 'text-red-500'
                    }`}>
                    <p className={'text-right text-sm'}>
                        <span className={'mr-1 font-medium text-black'}>
                            {t('translation:MY_ACCOUNT.MY_REPORTS.STATUS')}:
                        </span>
                        {t(`translation:REPORT_STATUS.${report.status}`)}
                    </p>

                    <span className={'ml-1 text-lg'}>
                        {report.status === 0 && <MdHelpOutline />}
                        {report.status === 1 && <MdOutlineThumbUpAlt />}
                        {report.status === 2 && <MdConstruction />}
                        {report.status === 3 && <MdOutlineCheckCircleOutline />}
                        {report.status === 4 && <MdRemoveCircleOutline />}
                    </span>
                </div>
                <small className={'mt-auto ml-auto text-right text-gray-500'}>
                    {new Date(report.createdAt).toLocaleDateString(i18next.language, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                </small>
            </div>
        </div>
    );
}
